import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Heart, ShieldCheck, Truck, Ruler } from "lucide-react";
import { toast } from "sonner";
import { Page, FadeUp, SectionHead } from "@/components/Reveal";
import { productById, PRODUCTS, inr, lineById } from "@/data/products";
import ZipCTA from "@/components/denim/ZipCTA";
import SizeGuide from "@/components/denim/SizeGuide";
import RivetCorners from "@/components/denim/RivetCorners";
import { cartAdd } from "@/components/denim/CartContext";
import { useWishlist } from "@/components/denim/WishlistContext";
import { playZip } from "@/lib/zipSound";

const SIZES = ["28", "30", "32", "34", "36", "38"];
const VIEWS = [
    { id: "full", label: "Full" },
    { id: "detail", label: "Detail" },
    { id: "stitch", label: "Stitch" },
];

export default function Product() {
    const { id } = useParams();
    const p = productById(id);
    const [size, setSize] = useState(null);
    const [view, setView] = useState("full");
    const [guide, setGuide] = useState(false);
    const { has, toggle } = useWishlist();

    if (!p) return <Navigate to="/shop" replace />;

    const line = lineById(p.line);
    const pct = Math.round((1 - p.now / p.was) * 100);
    const saved = has(p.id);
    const related = PRODUCTS.filter((r) => r.line === p.line && r.id !== p.id).slice(0, 4);
    const viewClass =
        view === "detail"
            ? "object-top scale-[1.5]"
            : view === "stitch"
            ? "object-[center_35%] scale-[1.15]"
            : "object-center";

    const addToBag = () => {
        if (!size) {
            toast.error("Please pick a size first");
            return;
        }
        cartAdd({ productId: p.id, name: p.name, size, price: p.now, img: p.img });
        playZip({ rate: 1.2, volume: 0.11 });
        toast.success(`${p.name} (Size ${size}) dropped in your pocket`);
    };

    return (
        <Page className="pb-28">
            <section className="mx-auto w-full max-w-[1400px] px-5 pt-32 md:px-8 md:pt-40 lg:px-10">
                <nav className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-mute" data-testid="product-breadcrumb">
                    <Link to="/" className="transition-colors hover:text-gold">Home</Link>
                    <span>/</span>
                    <Link to="/shop" className="transition-colors hover:text-gold">Collection</Link>
                    <span>/</span>
                    <span className="text-gold">{p.name}</span>
                </nav>
            </section>

            <section className="mx-auto grid w-full max-w-[1400px] gap-12 px-5 pt-8 md:px-8 lg:grid-cols-2 lg:px-10">
                <FadeUp>
                    <div className="relative aspect-[3/4] overflow-hidden border border-gold/25 bg-elev" data-testid="product-gallery">
                        <img
                            src={p.img}
                            alt={p.name}
                            className={`h-full w-full object-cover transition-all duration-700 ease-out ${viewClass}`}
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-obsidian/30 via-transparent to-transparent" />
                        <div className="pointer-events-none absolute inset-3 border-2 border-dashed border-gold/30" aria-hidden="true" />
                        <RivetCorners inset="10px" />
                        {pct > 0 && (
                            <span className="absolute left-4 top-4 bg-gold px-3 py-1 font-mono text-[11px] font-medium text-black">
                                {pct}% OFF
                            </span>
                        )}
                    </div>
                    <div className="mt-4 flex gap-3">
                        {VIEWS.map((v) => (
                            <button
                                key={v.id}
                                data-testid={`product-view-${v.id}`}
                                onClick={() => setView(v.id)}
                                className={`flex-1 border px-3 py-2.5 text-[10px] font-mono uppercase tracking-[0.2em] transition-colors ${
                                    view === v.id
                                        ? "border-gold bg-gold/10 text-gold"
                                        : "border-white/15 text-mute hover:border-gold/60 hover:text-ink"
                                }`}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                </FadeUp>

                <FadeUp delay={0.1}>
                    <div className="flex h-full flex-col">
                        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold">
                            {line ? line.name : "URWAN"} · Limited Edition
                        </p>
                        <h1 className="mt-3 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-ink md:text-5xl" data-testid="product-title">
                            {p.name}
                        </h1>
                        <div className="mt-4 flex items-baseline gap-3">
                            <span className="font-display text-3xl font-semibold text-gold">{inr(p.now)}</span>
                            {p.now < p.was && (
                                <span className="text-base text-mute line-through">{inr(p.was)}</span>
                            )}
                        </div>

                        <div className="mt-8 flex items-center justify-between">
                            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-mute">Select size</p>
                            <button
                                data-testid="product-size-guide-open"
                                onClick={() => setGuide(true)}
                                className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-gold transition-colors hover:text-ink"
                            >
                                <Ruler size={12} /> Size Guide
                            </button>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {SIZES.map((s) => (
                                <button
                                    key={s}
                                    data-testid={`product-size-${s}`}
                                    onClick={() => setSize(s)}
                                    className={`h-11 w-13 border font-mono text-sm transition-colors ${
                                        size === s
                                            ? "border-gold bg-gold text-black"
                                            : "border-white/15 text-ink hover:border-gold hover:text-gold"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>

                        <div className="mt-8 flex gap-3">
                            <ZipCTA type="button" testid="product-add-button" className="flex-1" onClick={addToBag}>
                                Add to Bag
                            </ZipCTA>
                            <button
                                data-testid="product-wishlist-button"
                                aria-label={saved ? "Remove from wishlist" : "Add to wishlist"}
                                onClick={() => {
                                    const added = toggle({ productId: p.id, name: p.name, price: p.now, img: p.img });
                                    toast[added ? "success" : "info"](
                                        added ? `${p.name} saved to your wishlist` : `${p.name} removed from wishlist`,
                                    );
                                }}
                                className={`grid h-14 w-14 shrink-0 place-items-center border transition-colors ${
                                    saved
                                        ? "border-gold bg-gold text-black"
                                        : "border-white/20 text-ink hover:border-gold hover:text-gold"
                                }`}
                            >
                                <Heart size={18} fill={saved ? "currentColor" : "none"} />
                            </button>
                        </div>

                        <div className="mt-8 space-y-3 border-t border-dashed border-gold/30 pt-6">
                            <p className="flex items-center gap-3 text-sm text-mute">
                                <ShieldCheck size={16} className="shrink-0 text-gold" />
                                Lifetime stitch &amp; tear warranty — protected against structural failures, for life.
                            </p>
                            <p className="flex items-center gap-3 text-sm text-mute">
                                <Truck size={16} className="shrink-0 text-gold" />
                                Complimentary shipping across India, always.
                            </p>
                        </div>

                        <p className="mt-6 text-sm leading-relaxed text-mute">
                            {line ? line.sub : "Engineered to outlive trends"}. Patterned by data for a
                            natural silhouette, built with reinforced stitching, and finished in
                            performance denim that ages with you — never against you.
                        </p>

                        <Link
                            to="/shop"
                            data-testid="product-back-link"
                            className="group mt-8 inline-flex w-fit items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-mute transition-colors hover:text-gold"
                        >
                            <ArrowLeft size={13} className="transition-transform group-hover:-translate-x-1" />
                            Back to Collection
                        </Link>
                    </div>
                </FadeUp>
            </section>

            {related.length > 0 && (
                <section className="mx-auto w-full max-w-[1400px] px-5 pt-24 md:px-8 lg:px-10">
                    <SectionHead eyebrow="Styled Together" title="Complete the Look" />
                    <div className="mt-12 grid grid-cols-2 gap-5 md:gap-8 lg:grid-cols-4" data-testid="product-related-grid">
                        {related.map((r, i) => (
                            <ProductCardLazy key={r.id} p={r} index={i} />
                        ))}
                    </div>
                </section>
            )}

            {guide && <SizeGuide selected={size} onClose={() => setGuide(false)} />}
        </Page>
    );
}

import ProductCard from "@/components/ProductCard";

function ProductCardLazy({ p, index }) {
    return (
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.6, delay: (index % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}>
            <ProductCard p={p} index={index} />
        </motion.div>
    );
}
